import { useEffect, useState } from "react";
import { fromTag } from "framerate-utils";

import { expiresIn } from "../../../utils/signedUrls";

export const SIGNED_URL_REFRESH_MS = 1000 * 60 * 60 * 2;

export function useWindowSize() {
	const [windowSize, setWindowSize] = useState({
		width: 0,
		height: 0,
	});

	useEffect(() => {
		function handleResize() {
			if (typeof window === "undefined") {
				return;
			}

			setWindowSize({
				width: window.innerWidth,
				height: window.innerHeight,
			});
		}

		if (typeof window === "undefined") {
			return;
		}

		window.addEventListener("resize", handleResize);
		handleResize();

		return () => window.removeEventListener("resize", handleResize);
	}, []);

	return windowSize;
}

export function off(obj: any, ...args: any[]) {
	if (obj?.removeEventListener) {
		obj.removeEventListener(...args);
	}
}

export function on(obj: any, ...args: any[]) {
	if (obj?.addEventListener) {
		obj.addEventListener(...args);
	}
}

export function clamp(value: number, min: number, max: number) {
	return Math.min(Math.max(value, min), max);
}

export function anyNotInThreshold(target: number, durations: number[], threshold: number) {
	return durations.some((duration) => Math.abs(duration - target) > threshold);
}

export function parseNumber(value: any) {
	const parsed = Number(value);
	return Number.isFinite(parsed) ? parsed : null;
}

export const normalizeFieldPath = (path: any) => {
	return String(path || "")
		.trim()
		.replace(/\[(\w+)\]/g, ".$1")
		.replace(/^\./, "");
};

export const getByPath = (obj: any, path: any) => {
	const normalizedPath = normalizeFieldPath(path);

	if (!normalizedPath) {
		return undefined;
	}

	const parts = normalizedPath.split(".");
	let current = obj;

	for (const part of parts) {
		if (!current || typeof current !== "object") {
			return undefined;
		}

		if (!Object.prototype.hasOwnProperty.call(current, part)) {
			return undefined;
		}

		current = current[part];
	}

	return current;
};

export const isHttpUrl = (value: any) => {
	return typeof value === "string" && (value.startsWith("http://") || value.startsWith("https://"));
};

export const isSignedUrlFieldPath = (fieldPath: any) => {
	return String(fieldPath || "").includes("_signedUrl");
};

export const isSignableStoragePath = (value: any) => {
	if (!value || typeof value !== "string") {
		return false;
	}

	if (value === "error") {
		return false;
	}

	if (isHttpUrl(value)) {
		return false;
	}

	if (value.startsWith("gs://")) {
		return false;
	}

	if (value.startsWith("/") || value.includes("..") || value.includes("?")) {
		return false;
	}

	return value.startsWith("versions/");
};

export const isFreshSignedUrl = (value: any) => {
	if (!value || typeof value !== "string") {
		return false;
	}

	return expiresIn(value) > SIGNED_URL_REFRESH_MS;
};

export const getSourceFromPath = (versionValues: any, pathField: string) => {
	const normalizedPathField = normalizeFieldPath(pathField);

	if (!normalizedPathField || isSignedUrlFieldPath(normalizedPathField)) {
		return null;
	}

	return {
		pathField: normalizedPathField,
		signedUrlField: `${normalizedPathField}_signedUrl`,
		path: getByPath(versionValues, normalizedPathField),
		signedUrl: getByPath(versionValues, `${normalizedPathField}_signedUrl`),
	};
};

export const normalizeTimecode = (value: any) => {
	const text = String(value || "").trim();

	if (/^\d{2}:\d{2}:\d{2}[:;]\d{2}$/.test(text)) {
		return text;
	}

	return "00:00:00:00";
};

export const frameRateFromFio = (fioFrameRate: number) => {
	if (!fioFrameRate) {
		return undefined;
	}

	const frameRateToString = fioFrameRate.toString();
	const split = frameRateToString.split(".");
	const tagOn = split[1]?.slice(0, 2) || "00";

	try {
		return fromTag("FPS_" + split[0] + tagOn);
	} catch {
		try {
			const rounded = Math.round(fioFrameRate).toString();
			return fromTag("FPS_" + rounded + "00");
		} catch {
			return undefined;
		}
	}
};

export const secondsToMedia = (secondsInput: number) => {
	let totalSeconds = secondsInput || 0;

	if (totalSeconds < 0) {
		totalSeconds = 0;
	}

	const hours = Math.floor(totalSeconds / 3600);
	totalSeconds %= 3600;

	const minutes = Math.floor(totalSeconds / 60);
	const seconds = Math.round(totalSeconds % 60);

	return hours < 1 ? `${minutes.toString().padStart(2, "0")}:${seconds.toString().padStart(2, "0")}` : `${hours.toString().padStart(2, "0")}:${minutes.toString().padStart(2, "0")}:${seconds.toString().padStart(2, "0")}`;
};
